
		import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "dreamland/jsx-runtime";
export const title = "MacDirtyCow app development: From zero to hero.";
export const tags = ["Tutorials", "Apple"];
export const image = "/blog-images/xc-starter.jpg";
export const description = "A walkthrough for building an app that leverages the MacDirtyCow exploit (MDC).";
function _createMdxContent(props) {
  const _components = {
    a: "a",
    blockquote: "blockquote",
    code: "code",
    h2: "h2",
    h3: "h3",
    img: "img",
    li: "li",
    p: "p",
    pre: "pre",
    span: "span",
    strong: "strong",
    ul: "ul",
    ...props.components
  };
  return _jsxs(_Fragment, {
    children: [_jsx(_components.h2, {
      children: "Wait. What exactly is MacDirtyCow?"
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.a, {
        href: "https://nvd.nist.gov/vuln/detail/CVE-2022-46689",
        children: "MacDirtyCow"
      }), ", often abbreviated as MDC, is a security vunerability that exploits a race condition in XNU's virtual memory system to overwrite the cached version of any given file. Because of its versatility, it has been used in a number of recent iOS customization apps such as Cowabunga."]
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsx(_components.p, {
        children: "I'm saying this like I'm some mega-nerd who knows how everything works, but in reality I don't even know how copy-on-write works 😭"
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "Now with that out of the way, let's get into how you can make your own app that leverages the MacDirtyCow exploit."
    }), "\n", _jsx(_components.h2, {
      children: "Prerequisites"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "A Mac with Xcode 14+ installed"
      }), "\n", _jsx(_components.li, {
        children: "An iOS device with an iOS version of 16.1.2 or below. I recommend anywhere from 15.0 to 16.1.2."
      }), "\n"]
    }), "\n", _jsx(_components.h2, {
      children: "Getting Started with your Xcode Project"
    }), "\n", _jsx(_components.p, {
      children: "Open Xcode. You should be presented with a screen similar to this:"
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.img, {
        src: "/blog-images/xcode-home.jpg",
        alt: "Xcode Startup Screen"
      })
    }), "\n", _jsx(_components.p, {
      children: "Click \"Create a new Xcode project\" to create a new project. Select \"App\" under the \"iOS\" tab. Click Next and you will be prompted to choose options for the project. Set it up similar to the image below, but make sure to change the Product Name to what you want your app to be called, and the Organization Identifier to whatever you want. Make sure Interface is set to SwiftUI and Language is set to Swift."
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.img, {
        src: "/blog-images/xc-proj-opts.jpg",
        alt: "Xcode Project Options"
      })
    }), "\n", _jsx(_components.p, {
      children: "Click next and save the directory wherever you want."
    }), "\n", _jsx(_components.p, {
      children: "Congratulations! You've made your first Xcode project."
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.img, {
        src: "/blog-images/xc-starter.jpg",
        alt: "Xcode Project"
      })
    }), "\n", _jsx(_components.h2, {
      children: "Adding MDC"
    }), "\n", _jsx(_components.p, {
      children: "Now here comes the fun part: adding the MacDirtyCow exploit."
    }), "\n", _jsx(_components.h3, {
      children: "Option 1: Using DirtyCowKit"
    }), "\n", _jsx(_components.p, {
      children: "Step 1: In Xcode, under the file menu, click \"Add Packages...\""
    }), "\n", _jsxs(_components.p, {
      children: ["Step 2: In the search bar, enter ", _jsx(_components.code, {
        children: "https://github.com/BomberFish/DirtyCowKit"
      }), ". Click \"Add Package\" and then \"Add Package\" (again) and it should add the package to your project."]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "OPTIONAL:"
      }), " Add the AbsoluteSolver package from the following link: ", _jsx(_components.code, {
        children: "https://github.com/BomberFish/AbsoluteSolver-iOS"
      }), ". Absolute Solver will automatically toggle between regular file operations using Swift's ", _jsx(_components.code, {
        children: "FileManager"
      }), " and an MDC-based operation."]
    }), "\n", _jsx(_components.h3, {
      children: "Option 2 (advanced, not recommended): Manually adding the exploit files"
    }), "\n", _jsxs(_components.p, {
      children: ["Step 1: Get the exploit files. You can easily find these from somewhere like the ", _jsx(_components.a, {
        href: "https://github.com/leminlimez/Cowabunga/tree/main/MacDirtyCowSwift/Exploit",
        children: "Cowabunga GitHub repository"
      }), "."]
    }), "\n", _jsx(_components.p, {
      children: "Step 2: Add the files to your Xcode project, making sure to include them in your bridging header. If you don't know what that is or are confused, you're best off using option 1. The steps below are for option 1 only."
    }), "\n", _jsx(_components.h2, {
      children: "Unsandboxing"
    }), "\n", _jsxs(_components.p, {
      children: ["At the top of your ", _jsx(_components.code, {
        children: "<Project name>App.swift"
      }), " file, type ", _jsx(_components.code, {
        children: "import DirtyCowKit"
      }), ". Then, under ", _jsx(_components.code, {
        children: "ContentView()"
      }), ", add the following code:"]
    }), "\n", _jsx(_components.pre, {
      children: _jsxs(_components.code, {
        class: "language-swift",
        children: [".", _jsx(_components.span, {
          class: "pl-c1",
          children: "onAppear"
        }), " {\n    ", _jsx(_components.span, {
          class: "pl-k",
          children: "if"
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "#available"
        }), "(", _jsx(_components.span, {
          class: "pl-k",
          children: "iOS"
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "16.2"
        }), ", ", _jsx(_components.span, {
          class: "pl-k",
          children: "*"
        }), ") {\n        ", _jsx(_components.span, {
          class: "pl-c1",
          children: "print"
        }), "(", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "Throwing not supported error (mdc patched)", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), ")\n        DispatchQueue.", _jsx(_components.span, {
          class: "pl-smi",
          children: "main"
        }), ".", _jsx(_components.span, {
          class: "pl-c1",
          children: "async"
        }), " {\n            ", _jsx(_components.span, {
          class: "pl-k",
          children: "let"
        }), " alert ", _jsx(_components.span, {
          class: "pl-k",
          children: "="
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "UIAlertController"
        }), "(", _jsx(_components.span, {
          class: "pl-c1",
          children: "title"
        }), ": ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "Not Supported", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), ", ", _jsx(_components.span, {
          class: "pl-c1",
          children: "message"
        }), ": ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "This version of iOS is not supported.", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), ", ", _jsx(_components.span, {
          class: "pl-c1",
          children: "preferredStyle"
        }), ": .", _jsx(_components.span, {
          class: "pl-smi",
          children: "alert"
        }), ")\n            alert.", _jsx(_components.span, {
          class: "pl-c1",
          children: "addAction"
        }), "(", _jsx(_components.span, {
          class: "pl-c1",
          children: "UIAlertAction"
        }), "(", _jsx(_components.span, {
          class: "pl-c1",
          children: "title"
        }), ": ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "OK", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), ", ", _jsx(_components.span, {
          class: "pl-c1",
          children: "style"
        }), ": .", _jsx(_components.span, {
          class: "pl-smi",
          children: "default"
        }), ", ", _jsx(_components.span, {
          class: "pl-c1",
          children: "handler"
        }), ": ", _jsx(_components.span, {
          class: "pl-c1",
          children: "nil"
        }), "))\n            UIApplication.", _jsx(_components.span, {
          class: "pl-smi",
          children: "shared"
        }), ".", _jsx(_components.span, {
          class: "pl-smi",
          children: "windows"
        }), ".", _jsx(_components.span, {
          class: "pl-c1",
          children: "first"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "?"
        }), ".", _jsx(_components.span, {
          class: "pl-smi",
          children: "rootViewController"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "?"
        }), ".", _jsx(_components.span, {
          class: "pl-c1",
          children: "present"
        }), "(alert, ", _jsx(_components.span, {
          class: "pl-c1",
          children: "animated"
        }), ": ", _jsx(_components.span, {
          class: "pl-c1",
          children: "true"
        }), ", ", _jsx(_components.span, {
          class: "pl-c1",
          children: "completion"
        }), ": ", _jsx(_components.span, {
          class: "pl-c1",
          children: "nil"
        }), ")\n        }\n    } ", _jsx(_components.span, {
          class: "pl-k",
          children: "else"
        }), " {\n        ", _jsx(_components.span, {
          class: "pl-c",
          children: "// grant r/w access"
        }), "\n        ", _jsx(_components.span, {
          class: "pl-k",
          children: "if"
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "#available"
        }), "(", _jsx(_components.span, {
          class: "pl-k",
          children: "iOS"
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "15"
        }), ", ", _jsx(_components.span, {
          class: "pl-k",
          children: "*"
        }), ") {\n            ", _jsx(_components.span, {
          class: "pl-c1",
          children: "print"
        }), "(", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "Escaping Sandbox...", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), ")\n            DispatchQueue.", _jsx(_components.span, {
          class: "pl-smi",
          children: "main"
        }), ".", _jsx(_components.span, {
          class: "pl-c1",
          children: "asyncAfter"
        }), "(", _jsx(_components.span, {
          class: "pl-c1",
          children: "deadline"
        }), ": .", _jsx(_components.span, {
          class: "pl-c1",
          children: "now"
        }), "() ", _jsx(_components.span, {
          class: "pl-k",
          children: "+"
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "0.15"
        }), ") {\n                ", _jsx(_components.span, {
          class: "pl-k",
          children: "do"
        }), " {\n                    ", _jsx(_components.span, {
          class: "pl-k",
          children: "try"
        }), " MacDirtyCow.", _jsx(_components.span, {
          class: "pl-c1",
          children: "unsandbox"
        }), "()\n                } ", _jsx(_components.span, {
          class: "pl-k",
          children: "catch"
        }), " {\n                    ", _jsx(_components.span, {
          class: "pl-k",
          children: "let"
        }), " alert ", _jsx(_components.span, {
          class: "pl-k",
          children: "="
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "UIAlertController"
        }), "(", _jsx(_components.span, {
          class: "pl-c1",
          children: "title"
        }), ": ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "Unsandboxing Error", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), ", ", _jsx(_components.span, {
          class: "pl-c1",
          children: "message"
        }), ": ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), _jsx(_components.span, {
            class: "pl-pse",
            children: "\\("
          }), "error.", _jsx(_components.span, {
            class: "pl-smi",
            children: "localizedDescription"
          }), _jsx(_components.span, {
            class: "pl-pse",
            children: ")"
          }), _jsx(_components.span, {
            class: "pl-cce",
            children: "\\n"
          }), "Please close the app and retry. If the problem persists, reboot your device.", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), ", ", _jsx(_components.span, {
          class: "pl-c1",
          children: "preferredStyle"
        }), ": .", _jsx(_components.span, {
          class: "pl-smi",
          children: "alert"
        }), ")\n                    UIApplication.", _jsx(_components.span, {
          class: "pl-smi",
          children: "shared"
        }), ".", _jsx(_components.span, {
          class: "pl-smi",
          children: "windows"
        }), ".", _jsx(_components.span, {
          class: "pl-c1",
          children: "first"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "?"
        }), ".", _jsx(_components.span, {
          class: "pl-smi",
          children: "rootViewController"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "?"
        }), ".", _jsx(_components.span, {
          class: "pl-c1",
          children: "present"
        }), "(alert, ", _jsx(_components.span, {
          class: "pl-c1",
          children: "animated"
        }), ": ", _jsx(_components.span, {
          class: "pl-c1",
          children: "true"
        }), ", ", _jsx(_components.span, {
          class: "pl-c1",
          children: "completion"
        }), ": ", _jsx(_components.span, {
          class: "pl-c1",
          children: "nil"
        }), ")\n                }\n            }\n        }\n    }\n} \n"]
      })
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.strong, {
        children: "Your file should look something like this:"
      })
    }), "\n", _jsx(_components.pre, {
      children: _jsxs(_components.code, {
        class: "language-swift",
        children: [_jsx(_components.span, {
          class: "pl-k",
          children: "import"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "MacDirtyCow"
        }), "\n", _jsx(_components.span, {
          class: "pl-k",
          children: "import"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "SwiftUI"
        }), "\n\n", _jsx(_components.span, {
          class: "pl-k",
          children: "@main"
        }), "\n", _jsx(_components.span, {
          class: "pl-k",
          children: "struct"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "DirtyCowExampleApp"
        }), ": ", _jsx(_components.span, {
          class: "pl-e",
          children: "App "
        }), "{\n    ", _jsx(_components.span, {
          class: "pl-k",
          children: "var"
        }), " body: ", _jsx(_components.span, {
          class: "pl-k",
          children: "some"
        }), " Scene {\n        ", _jsx(_components.span, {
          class: "pl-c1",
          children: "WindowGroup"
        }), " {\n            ", _jsx(_components.span, {
          class: "pl-c1",
          children: "ContentView"
        }), "()\n                .", _jsx(_components.span, {
          class: "pl-c1",
          children: "onAppear"
        }), " {\n                    ", _jsx(_components.span, {
          class: "pl-k",
          children: "if"
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "#available"
        }), "(", _jsx(_components.span, {
          class: "pl-k",
          children: "iOS"
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "16.2"
        }), ", ", _jsx(_components.span, {
          class: "pl-k",
          children: "*"
        }), ") {\n                        ", _jsx(_components.span, {
          class: "pl-c1",
          children: "print"
        }), "(", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "Throwing not supported error (mdc patched)", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), ")\n                        DispatchQueue.", _jsx(_components.span, {
          class: "pl-smi",
          children: "main"
        }), ".", _jsx(_components.span, {
          class: "pl-c1",
          children: "async"
        }), " {\n                            ", _jsx(_components.span, {
          class: "pl-k",
          children: "let"
        }), " alert ", _jsx(_components.span, {
          class: "pl-k",
          children: "="
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "UIAlertController"
        }), "(", _jsx(_components.span, {
          class: "pl-c1",
          children: "title"
        }), ": ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "Not Supported", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), ", ", _jsx(_components.span, {
          class: "pl-c1",
          children: "message"
        }), ": ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "This version of iOS is not supported.", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), ", ", _jsx(_components.span, {
          class: "pl-c1",
          children: "preferredStyle"
        }), ": .", _jsx(_components.span, {
          class: "pl-smi",
          children: "alert"
        }), ")\n                            alert.", _jsx(_components.span, {
          class: "pl-c1",
          children: "addAction"
        }), "(", _jsx(_components.span, {
          class: "pl-c1",
          children: "UIAlertAction"
        }), "(", _jsx(_components.span, {
          class: "pl-c1",
          children: "title"
        }), ": ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "OK", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), ", ", _jsx(_components.span, {
          class: "pl-c1",
          children: "style"
        }), ": .", _jsx(_components.span, {
          class: "pl-smi",
          children: "default"
        }), ", ", _jsx(_components.span, {
          class: "pl-c1",
          children: "handler"
        }), ": ", _jsx(_components.span, {
          class: "pl-c1",
          children: "nil"
        }), "))\n                            UIApplication.", _jsx(_components.span, {
          class: "pl-smi",
          children: "shared"
        }), ".", _jsx(_components.span, {
          class: "pl-smi",
          children: "windows"
        }), ".", _jsx(_components.span, {
          class: "pl-c1",
          children: "first"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "?"
        }), ".", _jsx(_components.span, {
          class: "pl-smi",
          children: "rootViewController"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "?"
        }), ".", _jsx(_components.span, {
          class: "pl-c1",
          children: "present"
        }), "(alert, ", _jsx(_components.span, {
          class: "pl-c1",
          children: "animated"
        }), ": ", _jsx(_components.span, {
          class: "pl-c1",
          children: "true"
        }), ", ", _jsx(_components.span, {
          class: "pl-c1",
          children: "completion"
        }), ": ", _jsx(_components.span, {
          class: "pl-c1",
          children: "nil"
        }), ")\n                        }\n                    } ", _jsx(_components.span, {
          class: "pl-k",
          children: "else"
        }), " {\n                        ", _jsx(_components.span, {
          class: "pl-c",
          children: "// grant r/w access"
        }), "\n                        ", _jsx(_components.span, {
          class: "pl-k",
          children: "if"
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "#available"
        }), "(", _jsx(_components.span, {
          class: "pl-k",
          children: "iOS"
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "15"
        }), ", ", _jsx(_components.span, {
          class: "pl-k",
          children: "*"
        }), ") {\n                            ", _jsx(_components.span, {
          class: "pl-c1",
          children: "print"
        }), "(", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "Escaping Sandbox...", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), ")\n                            DispatchQueue.", _jsx(_components.span, {
          class: "pl-smi",
          children: "main"
        }), ".", _jsx(_components.span, {
          class: "pl-c1",
          children: "asyncAfter"
        }), "(", _jsx(_components.span, {
          class: "pl-c1",
          children: "deadline"
        }), ": .", _jsx(_components.span, {
          class: "pl-c1",
          children: "now"
        }), "() ", _jsx(_components.span, {
          class: "pl-k",
          children: "+"
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "0.15"
        }), ") {\n                                ", _jsx(_components.span, {
          class: "pl-k",
          children: "do"
        }), " {\n                                    ", _jsx(_components.span, {
          class: "pl-k",
          children: "try"
        }), " MacDirtyCow.", _jsx(_components.span, {
          class: "pl-c1",
          children: "unsandbox"
        }), "()\n                                } ", _jsx(_components.span, {
          class: "pl-k",
          children: "catch"
        }), " {\n                                    ", _jsx(_components.span, {
          class: "pl-k",
          children: "let"
        }), " alert ", _jsx(_components.span, {
          class: "pl-k",
          children: "="
        }), " ", _jsx(_components.span, {
          class: "pl-c1",
          children: "UIAlertController"
        }), "(", _jsx(_components.span, {
          class: "pl-c1",
          children: "title"
        }), ": ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), "Unsandboxing Error", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), ", ", _jsx(_components.span, {
          class: "pl-c1",
          children: "message"
        }), ": ", _jsxs(_components.span, {
          class: "pl-s",
          children: [_jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          }), _jsx(_components.span, {
            class: "pl-pse",
            children: "\\("
          }), "error.", _jsx(_components.span, {
            class: "pl-smi",
            children: "localizedDescription"
          }), _jsx(_components.span, {
            class: "pl-pse",
            children: ")"
          }), _jsx(_components.span, {
            class: "pl-cce",
            children: "\\n"
          }), "Please close the app and retry. If the problem persists, reboot your device.", _jsx(_components.span, {
            class: "pl-pds",
            children: "\""
          })]
        }), ", ", _jsx(_components.span, {
          class: "pl-c1",
          children: "preferredStyle"
        }), ": .", _jsx(_components.span, {
          class: "pl-smi",
          children: "alert"
        }), ")\n                                    UIApplication.", _jsx(_components.span, {
          class: "pl-smi",
          children: "shared"
        }), ".", _jsx(_components.span, {
          class: "pl-smi",
          children: "windows"
        }), ".", _jsx(_components.span, {
          class: "pl-c1",
          children: "first"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "?"
        }), ".", _jsx(_components.span, {
          class: "pl-smi",
          children: "rootViewController"
        }), _jsx(_components.span, {
          class: "pl-k",
          children: "?"
        }), ".", _jsx(_components.span, {
          class: "pl-c1",
          children: "present"
        }), "(alert, ", _jsx(_components.span, {
          class: "pl-c1",
          children: "animated"
        }), ": ", _jsx(_components.span, {
          class: "pl-c1",
          children: "true"
        }), ", ", _jsx(_components.span, {
          class: "pl-c1",
          children: "completion"
        }), ": ", _jsx(_components.span, {
          class: "pl-c1",
          children: "nil"
        }), ")\n                                }\n                            }\n                        }\n                    }\n                }\n        }\n    }\n}\n"]
      })
    }), "\n", _jsx(_components.h2, {
      children: "Using MDC"
    }), "\n", _jsx(_components.p, {
      children: "Here are some examples of using MDC in your app."
    }), "\n", _jsx(_components.h3, {
      children: "How to replace a file"
    }), "\n", _jsx(_components.pre, {
      children: _jsxs(_components.code, {
        class: "language-swift",
        children: [_jsx(_components.span, {
          class: "pl-k",
          children: "import"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "MacDirtyCow"
        }), "\nMacDirtyCow.", _jsx(_components.span, {
          class: "pl-c1",
          children: "overwriteFileWithDataImpl"
        }), "(", _jsx(_components.span, {
          class: "pl-c1",
          children: "originPath"
        }), ": ", _jsx(_components.span, {
          class: "pl-c1",
          children: "String"
        }), ", ", _jsx(_components.span, {
          class: "pl-c1",
          children: "replacementData"
        }), ": Data)\n"]
      })
    }), "\n", _jsx(_components.h3, {
      children: "If you opted to add AbsoluteSolver earlier"
    }), "\n", _jsx(_components.pre, {
      children: _jsxs(_components.code, {
        class: "language-swift",
        children: [_jsx(_components.span, {
          class: "pl-k",
          children: "import"
        }), " ", _jsx(_components.span, {
          class: "pl-en",
          children: "AbsoluteSolver"
        }), "\nAbsoluteSolver.", _jsx(_components.span, {
          class: "pl-c1",
          children: "replace"
        }), "(", _jsx(_components.span, {
          class: "pl-c1",
          children: "at"
        }), ": URL, ", _jsx(_components.span, {
          class: "pl-c1",
          children: "with"
        }), ": NSData)\n"]
      })
    }), "\n", _jsx(_components.h2, {
      children: "What now?"
    }), "\n", _jsxs(_components.p, {
      children: ["I recommend using websites like ", _jsx(_components.a, {
        href: "https://www.hackingwithswift.com",
        children: "Hacking With Swift"
      }), " to up your Swift knowledge. Also make sure to look at the source code of other MDC applications like Cowabunga, ControlConfig or AppCommander for more advanced usage."]
    }), "\n", _jsx(_components.p, {
      children: "Otherwise, happy hacking!"
    }), "\n", _jsx(_components.h3, {
      children: "Revisions"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "2023-05-12: Minor updates"
      }), "\n", _jsx(_components.li, {
        children: "2023-09-26: Fix formatting"
      }), "\n"]
    })]
  });
}
export function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = props.components || ({});
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}


		export default function Page() {
			const {wrapper: MDXLayout} = this.components || ({});
			return (
				MDXLayout 
					? _jsx(MDXLayout, { children: [_createMdxContent(this)], ...this })
					: _createMdxContent(this)
			)
		}
	